import React, { useEffect, useState } from 'react';
import { MessageSquare, Building2, UtensilsCrossed, Tag, ShoppingCart, TrendingUp, Mail, Clock, Bike, Store, Gift } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STAT_CARDS = [
  { key: 'unread_contact', label: 'Unread Messages', icon: MessageSquare, link: '/admin/messages' },
  { key: 'unread_franchise', label: 'Unread Franchise', icon: Building2, link: '/admin/franchise' },
  { key: 'menu_items', label: 'Menu Items', icon: UtensilsCrossed, link: '/admin/menu' },
  { key: 'deals', label: 'Active Deals', icon: Tag, link: '/admin/deals' },
  { key: 'orders', label: 'Total Orders', icon: ShoppingCart, link: '/admin/orders' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [recap, setRecap] = useState(null);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const today = new Date().toLocaleDateString('en-CA');

  useEffect(() => { api.get('/admin/stats').then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    api.get(`/admin/reports/daily?date=${today}`).then((r) => setRecap(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailRecap = async () => {
    setSending(true);
    try {
      const r = await api.post('/admin/reports/daily/send');
      toast({ title: r.data.ok ? 'Recap emailed' : 'Email not configured', description: `${r.data.dayLabel} → ${r.data.sentTo}` });
    } catch (err) {
      toast({ title: 'Could not send recap', description: err?.response?.data?.detail || '' });
    } finally { setSending(false); }
  };

  const delta = (v) => (v === null || v === undefined ? null : (
    <span className={`text-xs font-bold ${v >= 0 ? 'text-emerald-600' : 'text-[#ED2E2E]'}`}>
      {v >= 0 ? '▲' : '▼'} {Math.abs(v)}% vs last week
    </span>
  ));

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl text-[#0F1C2E] mb-2">Dashboard</h1>
      <p className="text-gray-600 mb-8">Welcome back — here’s what’s happening at Wrappus today.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {STAT_CARDS.map((c) => (
          <a key={c.key} href={c.link} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow group">
            <div className="w-11 h-11 rounded-xl bg-[#ED2E2E] text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <c.icon size={20} />
            </div>
            <div className="text-3xl font-bold text-[#0F1C2E] mb-1">{stats[c.key] ?? '-'}</div>
            <div className="text-sm text-gray-500">{c.label}</div>
          </a>
        ))}
      </div>

      <div className="mt-10 bg-white rounded-2xl p-6 shadow-sm" data-testid="dashboard-recap">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2"><TrendingUp className="text-[#ED2E2E]" size={20} />
              <h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Today so far</h2>
            </div>
            <p className="text-gray-500 text-sm mt-1">A full recap of yesterday lands in your inbox every morning at 7 AM.</p>
          </div>
          <button onClick={emailRecap} disabled={sending} className="btn-wrappus text-sm disabled:opacity-50" data-testid="recap-send-btn">
            <Mail size={14} /> {sending ? 'Sending…' : 'Email yesterday\u2019s recap'}
          </button>
        </div>

        {!recap ? (
          <div className="text-sm text-gray-400">Loading today’s numbers…</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-2xl bg-[#FDF3EC] p-4">
              <div className="text-xs uppercase tracking-widest text-gray-500">Sales</div>
              <div className="text-2xl font-bold text-[#0F1C2E]" data-testid="recap-sales">${recap.sales.toFixed(2)}</div>
              {delta(recap.salesDelta)}
            </div>
            <div className="rounded-2xl bg-[#FDF3EC] p-4">
              <div className="text-xs uppercase tracking-widest text-gray-500">Orders</div>
              <div className="text-2xl font-bold text-[#0F1C2E]" data-testid="recap-orders">{recap.orders}</div>
              {delta(recap.ordersDelta)}
              <div className="text-xs text-gray-500 mt-1">Avg ${recap.avgOrder.toFixed(2)}</div>
            </div>
            <div className="rounded-2xl bg-[#FDF3EC] p-4 text-sm space-y-1">
              <div className="flex items-center gap-2 text-gray-600"><Store size={14} /> Pickup <b className="ml-auto text-[#0F1C2E]">{recap.split.pickup.orders}</b></div>
              <div className="flex items-center gap-2 text-gray-600"><Bike size={14} /> Delivery <b className="ml-auto text-[#0F1C2E]">{recap.split.delivery.orders}</b></div>
              <div className="flex items-center gap-2 text-gray-600"><Clock size={14} /> Busiest <b className="ml-auto text-[#0F1C2E]">{recap.busiestHour?.label || '—'}</b></div>
            </div>
            <div className="rounded-2xl bg-[#FDF3EC] p-4 text-sm space-y-1">
              <div className="flex items-center gap-2 text-gray-600"><Gift size={14} /> Points earned <b className="ml-auto text-[#0F1C2E]">{recap.pointsEarned}</b></div>
              <div className="flex items-center gap-2 text-gray-600"><Gift size={14} /> Points redeemed <b className="ml-auto text-[#0F1C2E]">{recap.pointsRedeemed}</b></div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4">
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Top sellers today</div>
              {recap.topItems.length === 0 ? (
                <div className="text-sm text-gray-400">No orders yet today.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {recap.topItems.map((it, i) => (
                    <div key={it.name} className="py-2 flex items-center justify-between text-sm">
                      <span className="text-[#0F1C2E]"><span className="text-gray-400 mr-2">{i + 1}.</span>{it.name}</span>
                      <span className="text-gray-500">{it.qty}× · ${it.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><TrendingUp className="text-[#ED2E2E]" size={20} /><h2 className="font-heading font-bold text-lg text-[#0F1C2E]">Quick Tips</h2></div>
        <ul className="space-y-2 text-sm text-gray-600">
          <li>• Update your <a href="/admin/menu" className="text-[#ED2E2E] font-semibold">menu items</a> and prices anytime.</li>
          <li>• Toggle deals on/off from the <a href="/admin/deals" className="text-[#ED2E2E] font-semibold">deals page</a>.</li>
          <li>• New contact and franchise messages appear here in real time.</li>
          <li>• View incoming orders and payment status under <a href="/admin/orders" className="text-[#ED2E2E] font-semibold">orders</a>.</li>
        </ul>
      </div>
    </div>
  );
}
